import { X } from 'lucide-react';
import { createPortal } from 'react-dom';
import { useEffect, useState } from 'react';

interface AdminInstructionModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSubmit: (instruction: string) => Promise<void> | void;
    loading?: boolean;
    title?: string;
    placeholder?: string;
    submitText?: string;
}

const AdminInstructionModal = ({
    isOpen,
    onClose,
    onSubmit,
    loading = false,
    title = 'Edit Prompt',
    placeholder = "Describe how you'd like AI to reply (optional). Example: User asked pricing. Show all plans and suggest Quick for budget users.",
    submitText = 'Generate Reply'
}: AdminInstructionModalProps) => {
    const [instruction, setInstruction] = useState('');

    useEffect(() => {
        if (isOpen) {
            setInstruction('');
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = '';
        }

        return () => {
            document.body.style.overflow = '';
        };
    }, [isOpen]);

    useEffect(() => {
        const onEscape = (e: KeyboardEvent) => {
            if (e.key === 'Escape') handleClose();
        };
        if (isOpen) {
            document.addEventListener('keydown', onEscape);
        }
        return () => document.removeEventListener('keydown', onEscape);
    }, [isOpen, loading]);

    const handleClose = () => {
        if (loading) return;
        onClose();
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (loading) return;
        await onSubmit(instruction.trim());
    };

    if (!isOpen) return null;

    return createPortal(
        <div className="fixed inset-0 z-[2147483000] flex items-center justify-center p-4 sm:p-5">
            <div
                onClick={handleClose}
                className="absolute inset-0 bg-black/75 backdrop-blur-md"
            />

            <form
                onSubmit={handleSubmit}
                role="dialog"
                aria-modal="true"
                aria-label={title}
                className="relative w-full max-w-3xl bg-cosmic-900/95 border border-white/25 rounded-3xl shadow-[0_24px_90px_rgba(0,0,0,0.6)] z-[100] overflow-hidden"
            >
                <div className="px-5 md:px-7 py-4 md:py-5 border-b border-white/15 flex items-center justify-between gap-4">
                    <h2 className="text-2xl md:text-4xl font-bold text-white font-serif leading-none">{title}</h2>
                    <button
                        type="button"
                        onClick={handleClose}
                        disabled={loading}
                        className="w-10 h-10 rounded-xl border border-white/20 bg-white/[0.06] hover:bg-white/[0.12] text-white/70 hover:text-white flex items-center justify-center transition-colors disabled:opacity-50"
                        aria-label="Close modal"
                    >
                        <X size={18} />
                    </button>
                </div>

                <div className="px-5 md:px-7 py-5 md:py-6">
                    <textarea
                        value={instruction}
                        onChange={(e) => setInstruction(e.target.value)}
                        placeholder={placeholder}
                        rows={4}
                        maxLength={1000}
                        className="w-full bg-black/25 border border-white/15 rounded-2xl px-4 py-4 text-white text-base leading-relaxed placeholder:text-white/45 focus:outline-none focus:border-purple-300/55 transition-colors resize-none"
                        disabled={loading}
                        autoFocus
                    />
                    <div className="mt-3 text-right text-xs text-purple-200/70">
                        {instruction.length}/1000
                    </div>
                </div>

                <div className="px-5 md:px-7 pb-5 md:pb-6 grid grid-cols-1 sm:grid-cols-2 gap-3 border-t border-white/10 pt-4">
                    <button
                        type="button"
                        onClick={handleClose}
                        disabled={loading}
                        className="h-12 md:h-14 rounded-2xl border border-white/20 bg-transparent text-white/85 hover:bg-white/[0.06] transition-colors text-base md:text-lg font-semibold"
                    >
                        Cancel
                    </button>
                    <button
                        type="submit"
                        disabled={loading}
                        className="h-12 md:h-14 rounded-2xl bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-500 hover:to-teal-500 text-white text-base md:text-lg font-semibold transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                    >
                        {loading ? 'Generating...' : submitText}
                    </button>
                </div>
            </form>
        </div>,
        document.body
    );
};

export default AdminInstructionModal;
